import { useEffect, useState } from "react";

/**
 * Variant configurations for the progress bar
 * Contains container and fill color classes for each variant
 */
const VARIANT_STYLES = {
  green: {
    container: "bg-pk-gray-2 border-pk-gray-4",
    fill: "bg-pk-green-4",
  },
  blue: {
    container: "bg-pk-gray-2 border-pk-gray-4",
    fill: "bg-pk-blue-4",
  },
  orange: {
    container: "bg-pk-gray-2 border-pk-gray-4",
    fill: "bg-pk-orange-2",
  },
  garnet: {
    container: "bg-pk-gray-2 border-pk-gray-4",
    fill: "bg-pk-garnet-4",
  },
  red: {
    container: "bg-pk-gray-2 border-pk-gray-4",
    fill: "bg-pk-red-4",
  },
  white: {
    container: "bg-pk-gray-2 border-pk-gray-4",
    fill: "bg-pk-gray-4",
  },
  yellow: {
    container: "bg-pk-gray-2 border-pk-gray-4",
    fill: "bg-pk-yellow-3",
  },
  purple: {
    container: "bg-pk-gray-2 border-pk-gray-4",
    fill: "bg-pk-purple-4",
  },
} as const;

type VariantKey = keyof typeof VARIANT_STYLES;

interface SegmentedProgressBarProps {
  /** Progress ratio between 0 and 1 */
  ratio: number;
  /** Color variant for the filled segments */
  variant?: VariantKey;
  /** Custom container variant styling */
  containerVariant?: VariantKey;
  /** Animation duration in milliseconds */
  animationDuration?: number;
  /** Maximum number of segments to display when full */
  maxSquares?: number;
  /** Additional container classes */
  className?: string;

  segmented?: boolean;
}

/**
 * A segmented progress bar with animated fill effect
 * Displays progress as a series of tiny squares that fill from left to right
 * Supports gradient effects across segments and mount animations
 */
export const SegmentedProgressBar = ({
  ratio,
  variant = "green",
  containerVariant,
  animationDuration = 500,
  maxSquares = 20,
  className = "",
  segmented = true,
}: SegmentedProgressBarProps) => {
  const [mounted, setMounted] = useState(false);

  // Trigger animation on mount
  useEffect(() => {
    setMounted(true);
  }, []);

  // Calculate number of filled squares based on ratio
  const filledSquares = Math.floor(ratio * maxSquares);

  // Get styling for container and fill
  const containerStyle =
    containerVariant !== undefined
      ? VARIANT_STYLES[containerVariant].container
      : VARIANT_STYLES[variant].container;
  const fillStyle = VARIANT_STYLES[variant].fill;

  return (
    <div
      className={`w-full rounded-lg border-4 h-8 flex items-center justify-start p-1 ${
        segmented ? "gap-1" : ""
      } ${containerStyle} ${className}`}
    >
      {Array.from({ length: maxSquares }).map((_, index) => {
        // Calculate gradient opacity (darker to lighter from left to right)
        const opacityPercent =
          index > filledSquares ? 0 : 100 - (index / filledSquares) * 60; // 100% to 70%

        // Calculate staggered animation delay
        const delay = (index / maxSquares) * animationDuration;

        return (
          <div
            key={index}
            className={`min-w-1 grow h-full ${
              segmented ? "rounded-lg" : ""
            } ${fillStyle} transition-all duration-150`}
            style={{
              opacity: mounted ? opacityPercent / 100 : 0,
              transform: mounted ? "scale(1)" : "scale(0)",
              transitionDelay: `${delay}ms`,
            }}
          />
        );
      })}
    </div>
  );
};
